import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import algoliasearch from 'algoliasearch/lite';
import { BaseHit, Hit } from 'instantsearch.js';
import { useCallback, useEffect, useState } from 'react';
import Autosuggest, {
  GetSuggestionValue,
  InputProps,
  OnSuggestionSelected,
  RenderSuggestion,
} from 'react-autosuggest';
import {
  Configure,
  Highlight,
  InstantSearch,
  SearchBox,
  Snippet,
  useHits,
  useSearchBox,
} from 'react-instantsearch';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { glossary } from '@sorare/core/src/lib/glossary';
import { OverrideClasses } from '@sorare/core/src/style/utils';

import { useZendeskLocale } from 'hooks/zendesk';
import { articlePath } from 'lib/path';

const [StyledAutosuggest, classes] = OverrideClasses(Autosuggest, null, {
  container: css`
    position: relative;
    line-height: 1;
    flex-grow: 1;
    width: 100%;
  `,
  suggestionsContainerOpen: css`
    position: absolute;
    z-index: 1300;
    background: var(--c-neutral-200);
    border: 1px solid var(--c-neutral-400);
    width: 100%;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
  `,
  suggestionsList: css`
    margin: 0;
    padding: 0;
    list-style-type: none;
  `,
  suggestion: css`
    display: block;
    border-bottom: 1px solid var(--c-neutral-400);

    &:last-child {
      border-bottom: none;
    }
  `,
});

interface ArticleHit extends BaseHit {
  id: string;
  body_safe: string;
  category: {
    id: string;
    title: string;
  };
  section: {
    id: string;
    title: string;
  };
  title: string;
}

const Suggestion = styled(Vertical).attrs({ gap: 1 })`
  padding: var(--unit);

  &:hover {
    cursor: pointer;
    background-color: var(--c-neutral-300);
  }

  & mark {
    color: var(--c-neutral-100);
    background-color: var(--c-static-yellow-100);
  }
`;

const Title = styled(Horizontal)`
  font-weight: var(--t-bold);
  flex-wrap: wrap;

  & * {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const Content = styled.div`
  font: var(--t-body-s);
`;

const Wrapper = styled.div`
  position: relative;
  margin-bottom: 2em;
`;

const renderSuggestion: RenderSuggestion<Hit<ArticleHit>> = (
  hit: Hit<ArticleHit>
) => {
  return (
    <Suggestion>
      <Title>
        <Highlight attribute="category.title" hit={hit} />
        <FontAwesomeIcon icon={faChevronRight} />
        <Highlight attribute="section.title" hit={hit} />
        <FontAwesomeIcon icon={faChevronRight} />
        <Highlight attribute="title" hit={hit} />
      </Title>
      <Content>
        <Snippet attribute="body_safe" hit={hit} />
      </Content>
    </Suggestion>
  );
};

const StyledSearchBox = styled(SearchBox)`
  & .ais-SearchBox-form {
    margin-bottom: 0;

    & button {
      display: none;
    }
  }

  & .ais-SearchBox-input {
    width: 100%;
    line-height: 2em;
    padding: var(--unit) var(--double-unit);
    border: none;
    background-color: var(--c-neutral-200);
  }
`;

const onSuggestionsFetchRequested = () => {};
const onSuggestionsClearRequested = () => {};

const Autocomplete = () => {
  const { formatMessage } = useIntl();
  const { items } = useHits<ArticleHit>();
  const { clear, query } = useSearchBox();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);

  const inputProps: InputProps<Hit<ArticleHit>> = {
    placeholder: formatMessage(glossary.search),
    onChange: () => {},
    value: query,
  };

  const onSuggestionSelected: OnSuggestionSelected<Hit<ArticleHit>> =
    useCallback(
      (_, { suggestion }) => {
        clear();
        setOpened(false);
        navigate(articlePath({ id: suggestion.id, title: suggestion.title }));
      },
      [clear, navigate]
    );

  const getSuggestionValue: GetSuggestionValue<Hit<ArticleHit>> =
    useCallback(() => {
      return query;
    }, [query]);

  const shouldRenderSuggestions = useCallback(() => {
    return opened;
  }, [opened]);

  useEffect(() => {
    setOpened(!!query);
  }, [query]);

  const onFocus = useCallback(() => {
    setOpened(!!query);
  }, [query]);

  return (
    <Wrapper>
      <StyledSearchBox
        onClick={onFocus}
        inputMode="search"
        placeholder={formatMessage(glossary.search)}
      />
      <StyledAutosuggest
        suggestions={opened ? items : []}
        alwaysRenderSuggestions
        shouldRenderSuggestions={shouldRenderSuggestions}
        theme={classes}
        getSuggestionValue={getSuggestionValue}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        renderSuggestion={renderSuggestion}
        renderInputComponent={() => null}
        inputProps={inputProps}
      />
    </Wrapper>
  );
};

const APPLICATION_ID = '7Z0Z8PASDY';
const API_KEY = '50488824068ead01625071cf944f53ee';
const INDEX_NAME = 'zendesk_sorare_articles';

const searchClient = algoliasearch(APPLICATION_ID, API_KEY);

export const Search = () => {
  const locale = useZendeskLocale();

  return (
    <InstantSearch searchClient={searchClient} indexName={INDEX_NAME}>
      <Configure
        hitsPerPage={5}
        facetFilters={`locale.locale:${locale}`}
        attributesToHighlight={['title', 'section.title', 'category.title']}
        attributesToSnippet={['body_safe:40']}
        snippetEllipsisText="..."
      />
      <Autocomplete />
    </InstantSearch>
  );
};
